<template>
  <div class="courses">
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">{{ pageContent.page_title || 'コース紹介' }}</h1>
      </div>
    </div>
    
    <section class="section">
      <div class="container">
        <div v-if="isLoading" class="loading-state">
          コース情報を読み込み中...
        </div>
        
        <div v-else>
          <div class="course-intro">
            <p>{{ pageContent.intro_text || '皆の英語教室では、年齢や目的に合わせたさまざまなコースをご用意しています。初心者から上級者まで、それぞれのレベルに合わせた指導を行っています。' }}</p>
          </div>
          
          <div class="course-list">
            <div 
              v-for="(course, index) in courses" 
              :key="course.id || index"
              class="course-item"
            >
              <div class="course-image">
                <img 
                  :src="getCourseImageUrl(course.image)" 
                  :alt="course.title"
                  @error="handleImageError"
                >
              </div>
              <div class="course-details">
                <h2 class="course-title">{{ course.title }}</h2>
                <div class="course-schedule" v-if="course.schedule">
                  <i class="el-icon-time"></i>
                  <span>{{ course.schedule }}</span>
                </div>
                <div class="course-description">
                  <div v-html="formatDescription(course.description)"></div>
                  <div v-if="course.target_audience">
                    <h3>こんな方におすすめ</h3>
                    <ul>
                      <li v-for="(target, idx) in course.target_audience" :key="idx">
                        {{ target }}
                      </li>
                    </ul>
                  </div>
                </div>
                <div class="course-price" v-if="course.price">
                  <div v-html="formatPrice(course.price)"></div>
                </div>
                <div class="course-actions">
                  <button 
                    @click="handleEnrollClick(course)" 
                    class="btn btn-primary btn-enrollment"
                    :disabled="isSubmittingEnrollment"
                  >
                    <i class="fas fa-graduation-cap"></i>
                    {{ isSubmittingEnrollment ? '処理中...' : 'このコースに申し込む' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <div class="cta-section">
            <h2>{{ pageContent.cta_title || '体験レッスン受付中！' }}</h2>
            <p>{{ pageContent.cta_text || 'どのコースも初回は無料で体験できます。お気軽にお問い合わせください。' }}</p>
            <router-link to="/contact" class="btn btn-large">お問い合わせ</router-link>
          </div>
        </div>
      </div>
    </section>
    
    <!-- 报名表单模态框 -->
    <div v-if="showEnrollmentModal" class="enrollment-modal" @click.self="closeEnrollmentModal">
      <div class="enrollment-modal-content">
        <div class="modal-header">
          <h3>コース申し込み</h3>
          <button @click="closeEnrollmentModal" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <form @submit.prevent="submitEnrollment">
            <div class="form-group">
              <label for="course-select">申し込みコース *</label>
              <select 
                id="course-select" 
                v-model="enrollmentForm.selectedCourse"
                required
                @change="updateSelectedCourse"
              >
                <option value="">コースを選択してください</option>
                <optgroup v-for="category in availableCourses" :key="category.name" :label="category.name">
                  <option 
                    v-for="course in category.courses" 
                    :key="course.value"
                    :value="course.value"
                  >
                    {{ course.label }} - ¥{{ course.price }}
                  </option>
                </optgroup>
              </select>
            </div>
            
            <div class="form-group">
              <label for="enroll-name">お名前 *</label>
              <input 
                type="text" 
                id="enroll-name" 
                v-model="enrollmentForm.name"
                required
                placeholder="山田太郎"
              >
            </div>
            
            <div class="form-group">
              <label for="enroll-age">年齢 *</label>
              <input 
                type="number" 
                id="enroll-age" 
                v-model="enrollmentForm.age"
                required
                min="1"
                max="150"
                placeholder="25"
              >
            </div>
            
            <div class="form-group">
              <label for="enroll-phone">電話番号 *</label>
              <input 
                type="tel" 
                id="enroll-phone" 
                v-model="enrollmentForm.phone"
                required
                placeholder="090-1234-5678"
              >
            </div>
            
            <div class="form-group">
              <label for="enroll-email">メールアドレス</label>
              <input 
                type="email" 
                id="enroll-email" 
                v-model="enrollmentForm.email"
                readonly
                :placeholder="currentUser ? currentUser.email : 'ログインが必要です'"
              >
              <p class="form-note">※ 登録済みのメールアドレスが自動入力されます</p>
            </div>
            
            <div class="form-group">
              <label for="enroll-message">メッセージ（任意）</label>
              <textarea 
                id="enroll-message" 
                v-model="enrollmentForm.message"
                rows="4"
                placeholder="ご質問やご要望がございましたらご記入ください"
              ></textarea>
            </div>
            
            <div class="form-actions">
              <button type="button" @click="closeEnrollmentModal" class="btn btn-outline">
                キャンセル
              </button>
              <button type="submit" class="btn btn-primary" :disabled="isSubmittingEnrollment">
                {{ isSubmittingEnrollment ? '申し込み中...' : '申し込む' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    
    <!-- 登录提示模态框 -->
    <div v-if="showLoginModal" class="login-modal" @click.self="closeLoginModal">
      <div class="login-modal-content">
        <div class="modal-header">
          <h3>ログインが必要です</h3>
          <button @click="closeLoginModal" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <p>コースに申し込むにはログインが必要です。</p>
          <div class="modal-actions">
            <button @click="closeLoginModal" class="btn btn-outline">キャンセル</button>
            <router-link to="/login" class="btn btn-primary">ログイン</router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { pageContentApi } from '@/api/pageContent'
import { getAvailableCourses, submitEnrollment, COURSE_CATEGORIES } from '@/api/courseEnrollment'
import { authService } from '@/api'

export default {
  name: 'CoursesPage',
  data() {
    return {
      isLoading: true,
      pageContent: {},
      courses: [],
      showEnrollmentModal: false,
      showLoginModal: false,
      isSubmittingEnrollment: false,
      currentUser: null,
      isAuthenticated: false,
      enrollmentForm: {
        selectedCourse: '',
        name: '',
        age: '',
        phone: '',
        email: '',
        message: ''
      },
      availableCourses: [],
      // 默认课程数据作为后备
      defaultCourses: [
        {
          id: 1,
          title: '子ども向け英語クラス',
          image: '@/assets/course/kids.png',
          schedule: '毎週木曜日 16:00 - 17:00 / 17:00 - 18:00',
          description: '遊びながら英語を学ぶ、子どものための英会話クラスです。ゲームやアクティビティーを通して、楽しく英語に触れることができます。\n\n歌やダンス、ストーリーテリングなど、子どもが夢中になれる活動を通して、自然と英語が身につきます。',
          target_audience: [
            '初めて英語を学ぶお子様',
            '英語に興味のあるお子様',
            '学校の英語の授業が苦手なお子様'
          ],
          price: '<strong>料金</strong>: 月額 5,200円（月4回）'
        },
        {
          id: 2,
          title: 'シニア向け英会話',
          image: '@/assets/course/senior.png',
          schedule: '毎週木曜日 10:00 - 11:00',
          description: '旅行や趣味に役立つ、実践的な英会話を学びます。初心者からでも安心して参加できるクラスです。\n\n日常会話や旅行で使える表現を中心に、リラックスした雰囲気で学べます。同年代の仲間と一緒に学ぶことで、楽しく継続できます。',
          target_audience: [
            '海外旅行を楽しみたい方',
            '趣味として英語を学びたい方',
            '脳トレとして言語学習をしたい方'
          ],
          price: '<strong>料金</strong>: 月2回x2ヶ月5200円'
        },
        {
          id: 3,
          title: '英検対策クラス',
          image: '@/assets/course/exam.png',
          schedule: '毎週木曜日 16:00 - 17:00 / 17:00 - 18:00',
          description: '英検合格に向けた対策クラスです。各級に合わせたカリキュラムで、効率的に学習できます。\n\n英検対策のプロが、リスニング、リーディング、ライティング、スピーキングをバランスよく指導します。過去問題の解説や模擬試験も実施します。',
          target_audience: [],
          price: '<strong>料金</strong>:\n5級以上の方 5500円\n4級以上の方 6500円\n3級以上の方 7000円\n準2級以上の方 7500円\n準2級プラス以上の方 8500円\n2級以上の方 9000円\n\n当教室の英検合格率は95%以上！確かな実績があります。'
        }
      ]
    }
  },
  async mounted() {
    await this.checkAuthStatus()
    await this.loadPageContent()
    await this.loadAvailableCourses()
  },
  methods: {
    // 检查用户认证状态
    async checkAuthStatus() {
      try {
        this.isAuthenticated = authService.isLoggedIn()
        if (this.isAuthenticated) {
          this.currentUser = await authService.getCurrentUser()
        }
      } catch (error) {
        console.error('检查认证状态失败:', error)
        this.isAuthenticated = false
        this.currentUser = null
      }
    },
    
    async loadPageContent() {
      try {
        this.isLoading = true
        const response = await pageContentApi.getPageContent('courses')
        
        if (response.success) {
          this.pageContent = response.data
          
          // 解析课程数据
          if (this.pageContent.courses_data) {
            try {
              this.courses = JSON.parse(this.pageContent.courses_data)
            } catch (e) {
              console.warn('Failed to parse courses data, using defaults:', e)
              this.courses = this.defaultCourses
            }
          } else {
            this.courses = this.defaultCourses
          }
        } else {
          console.warn('Failed to load page content:', response.error)
          this.courses = this.defaultCourses
          this.pageContent = {}
        }
      } catch (error) {
        console.error('Error loading page content:', error)
        this.courses = this.defaultCourses
        this.pageContent = {}
      } finally {
        this.isLoading = false
      }
    },
    
    async loadAvailableCourses() {
      try {
        const response = await getAvailableCourses()
        if (response.success) {
          this.formatAvailableCourses(response.data)
        }
      } catch (error) {
        console.error('获取课程列表失败:', error)
        // 使用默认课程数据
        this.formatDefaultCourses()
      }
    },
    
    formatAvailableCourses(courses) {
      const categorizedCourses = {}
      
      courses.forEach(course => {
        const categoryKey = course.category
        const categoryInfo = COURSE_CATEGORIES[categoryKey]
        
        if (!categorizedCourses[categoryKey]) {
          categorizedCourses[categoryKey] = {
            name: categoryInfo ? categoryInfo.name : categoryKey,
            courses: []
          }
        }
        
        let label = course.name
        if (course.subcategory && categoryInfo && categoryInfo.subcategories) {
          const subcategoryName = categoryInfo.subcategories[course.subcategory]
          label = `${course.name} (${subcategoryName})`
        }
        
        categorizedCourses[categoryKey].courses.push({
          value: `${course.category}:${course.subcategory || ''}`,
          label: label,
          price: course.price,
          course: course
        })
      })
      
      this.availableCourses = Object.values(categorizedCourses)
    },
    
    formatDefaultCourses() {
      // 使用默认数据格式化课程选项
      this.availableCourses = [
        {
          name: '子ども向け英語クラス',
          courses: [{
            value: 'kids:',
            label: '子ども向け英語クラス',
            price: 5200
          }]
        },
        {
          name: 'シニア向け英会話',
          courses: [{
            value: 'senior:',
            label: 'シニア向け英会話',
            price: 5200
          }]
        },
        {
          name: '英検対策クラス',
          courses: [
            { value: 'eiken:5kyu', label: '英検5級対策', price: 5500 },
            { value: 'eiken:4kyu', label: '英検4級対策', price: 6500 },
            { value: 'eiken:3kyu', label: '英検3級対策', price: 7000 },
            { value: 'eiken:jun2kyu', label: '英検準2級対策', price: 7500 },
            { value: 'eiken:jun2kyu_plus', label: '英検準2級プラス対策', price: 8500 },
            { value: 'eiken:2kyu', label: '英検2級対策', price: 9000 }
          ]
        }
      ]
    },
    
    handleEnrollClick(course) {
      if (!this.isAuthenticated) {
        this.showLoginModal = true
        return
      }
      
      // 预填用户邮箱
      this.enrollmentForm.email = this.currentUser.email
      
      // 预选择对应的课程（如果可能的话）
      this.preselectCourse(course)
      
      this.showEnrollmentModal = true
    },
    
    preselectCourse(course) {
      // 尝试根据课程标题预选择对应的选项
      const courseTitle = course.title.toLowerCase()
      
      if (courseTitle.includes('子ども') || courseTitle.includes('キッズ')) {
        this.enrollmentForm.selectedCourse = 'kids:'
      } else if (courseTitle.includes('シニア')) {
        this.enrollmentForm.selectedCourse = 'senior:'
      } else if (courseTitle.includes('英検')) {
        // 英检课程保持空选择，让用户自己选择级别
        this.enrollmentForm.selectedCourse = ''
      }
    },
    
    updateSelectedCourse() {
      // 当用户选择课程时可以添加额外的逻辑
    },
    
    async submitEnrollment() {
      if (!this.enrollmentForm.selectedCourse) {
        alert('コースを選択してください。')
        return
      }
      
      this.isSubmittingEnrollment = true
      
      try {
        const [category, subcategory] = this.enrollmentForm.selectedCourse.split(':')
        
        const enrollmentData = {
          name: this.enrollmentForm.name,
          age: parseInt(this.enrollmentForm.age),
          phone: this.enrollmentForm.phone,
          course_category: category,
          course_subcategory: subcategory || null,
          message: this.enrollmentForm.message
        }
        
        const response = await submitEnrollment(enrollmentData)
        
        if (response.success) {
          alert('申し込みが完了しました！確認メールをお送りいたします。審査結果についても後日ご連絡いたします。')
          this.closeEnrollmentModal()
          this.resetEnrollmentForm()
        } else {
          // 如果后端明确返回 success: false，也作为错误处理
          throw new Error(response.message || '申し込みに失敗しました');
        }
      } catch (error) {
        console.error('申し込みエラー:', error);
        // error.message 现在应该直接是后端返回的错误信息或通用HTTP错误
        const backendMessage = error.message;

        if (backendMessage && (backendMessage.includes('You have already enrolled in this course') || backendMessage.includes('already enrolled'))) {
          alert('このコースにはすでに申し込み済みです。');
        } else {
          alert('申し込みに失敗しました: ' + (backendMessage || 'しばらくしてから再度お試しください。'));
        }
      } finally {
        this.isSubmittingEnrollment = false;
      }
    },
    
    closeEnrollmentModal() {
      this.showEnrollmentModal = false
    },
    
    closeLoginModal() {
      this.showLoginModal = false
    },
    
    resetEnrollmentForm() {
      this.enrollmentForm = {
        selectedCourse: '',
        name: '',
        age: '',
        phone: '',
        email: this.currentUser ? this.currentUser.email : '',
        message: ''
      }
    },
    
    formatDescription(description) {
      if (!description) return ''
      return description.replace(/\n/g, '<br>')
    },
    
    formatPrice(price) {
      if (!price) return ''
      return price.replace(/\n/g, '<br>')
    },
    
    handleImageError(event) {
      // 如果图片加载失败，使用默认图片
      event.target.src = require('@/assets/course/kids.png')
    },
    
    getCourseImageUrl(image) {
      if (image) {
        return image
      } else {
        return require('@/assets/course/kids.png')
      }
    }
  }
}
</script>

<style scoped>
.page-header {
  background-color: var(--primary-color);
  color: white;
  padding: 60px 0;
  text-align: center;
}

.page-title {
  font-size: 32px;
  font-weight: bold;
}

.course-intro {
  text-align: center;
  max-width: 800px;
  margin: 0 auto 50px;
  font-size: 18px;
  line-height: 1.6;
  color: #555;
}

.course-list {
  margin-bottom: 50px;
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 60px;
}

.course-item {
  display: flex;
  background: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
}

.course-image {
  width: 30%;
  min-width: 300px;
  background-color: var(--accent-color);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.course-image img {
  width: 100%;
  height: auto;
}

.course-details {
  flex: 1;
  padding: 30px;
}

.course-title {
  font-size: 24px;
  color: var(--primary-color);
  margin-bottom: 15px;
}

.course-schedule {
  display: flex;
  align-items: flex-start;
  color: #666;
  margin-bottom: 20px;
}

.course-schedule i {
  margin-right: 10px;
  margin-top: 3px;
}

.course-description {
  margin-bottom: 20px;
}

.course-description p {
  margin-bottom: 15px;
  line-height: 1.6;
}

.course-description h3 {
  font-size: 18px;
  margin: 20px 0 10px;
  color: #333;
}

.course-description ul {
  padding-left: 20px;
  margin-bottom: 15px;
}

.course-description li {
  margin-bottom: 5px;
}

.course-price {
  font-size: 18px;
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 5px;
  margin-bottom: 20px;
}

.course-actions {
  display: flex;
  justify-content: flex-start;
}

.btn-enrollment {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.btn-enrollment:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 139, 171, 0.3);
}

.btn-enrollment:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.cta-section {
  text-align: center;
  padding: 50px 0;
  background-color: var(--light-bg);
  border-radius: 10px;
}

.cta-section h2 {
  font-size: 24px;
  margin-bottom: 15px;
  color: #333;
}

.cta-section p {
  margin-bottom: 25px;
  color: #555;
}

.btn-large {
  padding: 12px 30px;
  font-size: 18px;
}

/* 模态框样式 */
.enrollment-modal,
.login-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.enrollment-modal-content,
.login-modal-content {
  background: white;
  border-radius: 10px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  color: var(--primary-color);
}

.close-btn {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #666;
  padding: 5px;
}

.close-btn:hover {
  color: #333;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #333;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--primary-color);
}

.form-group input[readonly] {
  background-color: #f5f5f5;
  color: #666;
}

.form-note {
  font-size: 14px;
  color: #666;
  margin-top: 5px;
  margin-bottom: 0;
}

.form-actions,
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  text-decoration: none;
  display: inline-block;
  text-align: center;
  transition: all 0.3s ease;
}

.btn-primary {
  background-color: var(--primary-color);
  color: white;
}

.btn-primary:hover {
  background-color: var(--secondary-color);
}

.btn-primary:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.btn-outline {
  background-color: transparent;
  color: var(--primary-color);
  border: 1px solid var(--primary-color);
}

.btn-outline:hover {
  background-color: var(--primary-color);
  color: white;
}

.loading-state {
  text-align: center;
  padding: 60px 20px;
  color: #666;
  font-size: 18px;
}

@media (max-width: 900px) {
  .course-item {
    flex-direction: column;
  }
  
  .course-image {
    width: 100%;
    min-width: auto;
  }
  
  .enrollment-modal-content,
  .login-modal-content {
    width: 95%;
    margin: 10px;
  }
  
  .modal-body {
    padding: 15px;
  }
  
  .form-actions,
  .modal-actions {
    flex-direction: column;
  }
  
  .btn {
    width: 100%;
  }
}
</style>